import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import AdminSectionDivider from './AdminSectionDivider';
import { NavigationModule, NavigationPage, NavigationSubModule } from './types';

/**
 * Checks if a group header should be rendered before this page.
 * Returns the group label if this page starts a new group, otherwise null.
 */
function getGroupHeader(pages: NavigationPage[], index: number): string | null {
  const page = pages[index];
  if (!page.group) return null;
  if (index === 0) return page.group;
  const prev = pages[index - 1];
  return prev.group !== page.group ? page.group : null;
}

/**
 * Sidebar entries whose drill-down pages have no entry of their own. A page below one of them
 * keeps that entry highlighted and its module open (`/deliveries/orders/4611` → Orders); without
 * this the sidebar lost its place on every detail page. Listed one by one on purpose: a blanket
 * prefix rule would light two entries where a child path has its own entry.
 */
const PREFIX_MATCHED_PATHS = ['/deliveries/orders', '/deliveries/trips', '/deliveries/reports'];

function matchesPath(pagePath: string, locationPath: string): boolean {
  if (locationPath === pagePath) return true;
  if (PREFIX_MATCHED_PATHS.includes(pagePath) && locationPath.startsWith(`${pagePath}/`)) return true;
  // Match profile sub-routes (e.g., /profile/123/skills highlights "My Profile")
  if (pagePath.startsWith('/profile/') && locationPath.startsWith('/profile/')) {
    const pathBase = pagePath.split('/').slice(0, 3).join('/');
    const locationBase = locationPath.split('/').slice(0, 3).join('/');
    return pathBase === locationBase;
  }
  return false;
}

interface SidebarProps {
  /** Navigation modules to display (already filtered by role) */
  modules: NavigationModule[];
  /**
   * True while the company's module/page config is still in flight.
   *
   * 🔴 WHY THIS EXISTS. `useFilteredNavigationState` starts with an EMPTY company config, and an
   * empty config means "all modules enabled" (the backwards-compatible reading for a tenant that
   * predates module gating). So for the first moments of every page load the sidebar rendered the
   * FULL module list and then visibly collapsed to whatever the tenant actually bought — showing
   * people modules they do not have, on every navigation.
   *
   * This is deliberately NOT gated on "the fetch succeeded": the hook sets its loaded flag on
   * success AND on error, exactly like `subscriptionReady` in MasterLayout, because a tenant must
   * never be stuck behind a loading placeholder. On a failed fetch the config stays empty, the app
   * fails open to the full list, and the skeleton still clears.
   */
  isLoadingModules?: boolean;
  /** Whether the sidebar is collapsed (desktop only) */
  isCollapsed: boolean;
  /** Toggle function for collapse/expand or close mobile sidebar */
  onToggle: () => void;
  /**
   * Which of the two shells this instance IS. MasterLayout mounts one `desktop` instance
   * permanently and one `mobile` instance only while the hamburger has opened it.
   *
   * 🔴 WHY THIS IS A PROP AND NOT A WIDTH CHECK. This component used to decide "mobile or
   * desktop" from `window.innerWidth` on its own, so at 390px the PERMANENT desktop instance
   * (whose `isCollapsed` is the desktop expand/collapse preference, default expanded = false)
   * took the `isMobile && !isCollapsed` branch and painted the overlay drawer + `bg-black/50`
   * backdrop on every page load, covering the header language switch until it was tapped away.
   * The desktop preference must never drive the mobile drawer; only `isMobileSidebarOpen` may.
   */
  variant?: 'desktop' | 'mobile';
}

/**
 * Sidebar navigation component with collapsible modules and nested sub-modules.
 * Supports both desktop (collapsible) and mobile (overlay) modes. RTL via Tailwind.
 */
const Sidebar: React.FC<SidebarProps> = ({ modules, isCollapsed, onToggle, isLoadingModules = false, variant = 'desktop' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedModules, setExpandedModules] = useState<string[]>(['home']);
  // Sub-module keys are `${moduleId}:${subModuleId}` to avoid id collisions across modules.
  const [expandedSubModules, setExpandedSubModules] = useState<string[]>([]);
  const isMobile = variant === 'mobile';

  // Auto-expand the module (and sub-module) containing the active page.
  // Uses functional setters so we don't depend on the current state inside the effect.
  useEffect(() => {
    const activeModule = modules.find(module =>
      module.pages.some(page => matchesPath(page.path, location.pathname)) ||
      module.subModules?.some(sub => sub.pages.some(page => matchesPath(page.path, location.pathname)))
    );
    if (!activeModule) return;
    setExpandedModules(prev =>
      prev.includes(activeModule.id) ? prev : [...prev, activeModule.id]
    );
    const activeSub = activeModule.subModules?.find(sub =>
      sub.pages.some(page => matchesPath(page.path, location.pathname))
    );
    if (activeSub) {
      const key = `${activeModule.id}:${activeSub.id}`;
      setExpandedSubModules(prev => (prev.includes(key) ? prev : [...prev, key]));
    }
  }, [location.pathname, modules]);

  const toggleModule = (moduleId: string) => {
    if (isCollapsed) {
      // Expand sidebar and ensure the clicked module is open
      onToggle();
      setExpandedModules(prev =>
        prev.includes(moduleId) ? prev : [...prev, moduleId]
      );
      return;
    }
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const toggleSubModule = (moduleId: string, subModuleId: string) => {
    const key = `${moduleId}:${subModuleId}`;
    setExpandedSubModules(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const isActivePage = (path: string) => matchesPath(path, location.pathname);

  const isModuleActive = (module: NavigationModule) => {
    if (module.pages.some(page => isActivePage(page.path))) return true;
    return module.subModules?.some(sub => sub.pages.some(page => isActivePage(page.path))) ?? false;
  };

  const isSubModuleActive = (sub: NavigationSubModule) =>
    sub.pages.some(page => isActivePage(page.path));

  // `modules` is already role-filtered, so the divider lands above whichever admin module survives.
  const firstAdminModuleId = modules.find(m => m.isAdmin)?.id;

  const handlePageClick = (path: string, closeMobile: boolean = false) => {
    navigate(path);
    if (closeMobile && isMobile) {
      onToggle();
    }
  };

  /**
   * The nav placeholder. Mirrors the MODULE-ROW geometry (px-4 py-3, an 8x8 icon tile, a label) so
   * the real list lands in the same place the blocks occupied — a skeleton whose shape disagrees
   * with its content just relocates the flash rather than removing it.
   *
   * Six rows is a deliberate median, not a guess at any one tenant: the count is unknowable before
   * the config answers, and over-tall is a shorter correction than over-short. It is decorative and
   * announced by the live region below, so every block is aria-hidden.
   */
  const NavSkeleton: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
    <div
      className="py-4"
      data-testid="nav-modules-skeleton"
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={t('Loading navigation')}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className={`flex items-center px-4 py-3 ${collapsed ? 'justify-center' : 'space-x-3 rtl:space-x-reverse'}`}
          aria-hidden="true"
        >
          <div className="w-8 h-8 rounded-lg bg-muted animate-pulse motion-reduce:animate-none flex-shrink-0" />
          {!collapsed && (
            <div
              className="h-3 rounded bg-muted animate-pulse motion-reduce:animate-none"
              /* Varied widths so it reads as a list of names rather than a stack of identical
                 bars, which is the tell that makes a skeleton look like a broken layout. */
              style={{ width: `${[68, 52, 76, 60, 44, 70][i]}%` }}
            />
          )}
        </div>
      ))}
    </div>
  );

  /** Renders a leaf page row. `indentClass` controls how deep the row sits (direct page vs sub-module page). */
  const renderPage = (
    page: NavigationPage,
    indentClass: string,
    closeMobileOnClick: boolean
  ) => (
    <button
      key={page.path}
      onClick={() => handlePageClick(page.path, closeMobileOnClick)}
      aria-current={isActivePage(page.path) ? 'page' : undefined}
      className={`w-full flex items-center justify-between px-4 py-2.5 ${indentClass} text-sm text-start transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        page.comingSoon ? 'opacity-75 ' : ''
      }${
        isActivePage(page.path)
          ? 'bg-primary/10 text-primary font-medium border-s-2 border-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      <div className="flex items-center space-x-2 rtl:space-x-reverse">
        <Icon name={page.icon as any} size={14} />
        <span>{t(page.title)}</span>
        {page.comingSoon && (
          <span className="ms-2 text-[10px] font-medium bg-warning/10 text-warning px-1.5 py-0.5 rounded-full whitespace-nowrap">
            {t('Soon')}
          </span>
        )}
      </div>
      {page.badge != null && page.badge > 0 && (
        <span className="bg-error text-error-foreground text-xs font-semibold px-2 py-0.5 rounded-full">
          {page.badge}
        </span>
      )}
    </button>
  );

  /** Renders the expanded body of a module: direct pages (with group labels) + nested sub-modules. */
  const renderModuleBody = (module: NavigationModule, closeMobileOnClick: boolean) => (
    <div className="bg-muted/50">
      {/* Direct pages with optional group labels */}
      {module.pages.map((page, index) => {
        const groupHeader = getGroupHeader(module.pages, index);
        return (
          <React.Fragment key={page.path}>
            {groupHeader && (
              <div className="px-4 py-2 ps-16 mt-1 first:mt-0">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {t(groupHeader)}
                </span>
              </div>
            )}
            {renderPage(page, 'ps-16', closeMobileOnClick)}
          </React.Fragment>
        );
      })}

      {/* Nested sub-modules */}
      {module.subModules?.map(sub => {
        const key = `${module.id}:${sub.id}`;
        const isExpanded = expandedSubModules.includes(key);
        const active = isSubModuleActive(sub);
        return (
          <div key={sub.id}>
            <button
              onClick={() => toggleSubModule(module.id, sub.id)}
              className={`w-full flex items-center justify-between px-4 py-2.5 ps-10 text-sm text-start transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                active
                  ? 'text-primary font-medium'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                {sub.icon && (
                  <Icon name={sub.icon as any} size={14} className={active ? 'text-primary' : 'text-muted-foreground'} />
                )}
                <span className="font-medium text-xs uppercase tracking-wider">{t(sub.title)}</span>
              </div>
              <Icon
                name="ChevronDown"
                size={14}
                className={`transition-transform ${isExpanded ? 'rotate-180' : ''} ${active ? 'text-primary' : 'text-muted-foreground'}`}
              />
            </button>
            {isExpanded && (
              <div className="bg-background/40">
                {sub.pages.map(page => renderPage(page, 'ps-20', closeMobileOnClick))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Mobile sidebar overlay. Mounted by MasterLayout ONLY while `isMobileSidebarOpen`, so being
  // rendered at all means "open"; `md:hidden` keeps a drawer left open across a resize to desktop
  // from painting over the permanent desktop rail.
  if (isMobile) {
    return (
      <>
        {/* Overlay backdrop */}
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onToggle}
        />
        {/* Mobile Sidebar */}
        <aside className="fixed start-0 top-0 bottom-0 w-72 bg-card border-e border-border flex flex-col z-50 transform transition-transform duration-300 md:hidden">
          {/* Logo & Close Button */}
          <div className="h-16 min-h-16 flex-shrink-0 flex items-center justify-between px-4 border-b border-border">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <img
                src="/images/logo.png"
                alt="Mowazaf Logo"
                className="h-8 w-auto object-contain"
              />
            </div>
            <button
              onClick={onToggle}
              className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {/* 🔴 The skeleton REPLACES the list; it does not sit above it. While the config is in
                flight `modules` is not empty — it is the FULL role-visible set, because an empty
                company config reads as "all modules enabled". That full list IS the flash, so
                rendering it alongside a skeleton would change nothing. */}
            {isLoadingModules ? <NavSkeleton collapsed={false} /> : modules.map((module) => (
              <div key={module.id}>
                {module.id === firstAdminModuleId && <AdminSectionDivider />}

                {/* Module Header */}
                <button
                  data-testid={`nav-module-${module.id}`}
                  onClick={() => toggleModule(module.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    isModuleActive(module)
                      ? 'bg-primary/5 text-primary'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    <div className={`w-8 h-8 bg-gradient-to-br ${module.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon name={module.icon as any} size={16} color="white" />
                    </div>
                    <span className="font-medium text-sm">{t(module.title)}</span>
                  </div>
                  <Icon
                    name="ChevronDown"
                    size={16}
                    className={`transition-transform ${
                      expandedModules.includes(module.id) ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Module Body */}
                {expandedModules.includes(module.id) && renderModuleBody(module, true)}
              </div>
            ))}
          </nav>
        </aside>
      </>
    );
  }

  // Desktop sidebar (hidden on mobile)
  return (
    <aside
      className={`${
        isCollapsed ? 'w-20' : 'w-72'
      } bg-card border-e border-border flex-col flex-shrink-0 transition-all duration-300 ease-in-out hidden md:flex h-full overflow-hidden`}
    >
      {/* Logo & Toggle */}
      <div className="h-16 min-h-16 flex-shrink-0 flex items-center justify-between px-4 border-b border-border">
        {isCollapsed ? (
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center p-1 rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            title={t('Expand sidebar')}
          >
            <img
              src="/images/logo.png"
              alt="Mowazaf Logo"
              className="h-8 w-auto object-contain"
            />
          </button>
        ) : (
          <>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <img
                src="/images/logo.png"
                alt="Mowazaf Logo"
                className="h-8 w-auto object-contain"
              />
            </div>
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              title={t('Collapse sidebar')}
            >
              <Icon
                name="ChevronLeft"
                size={20}
                className="text-muted-foreground rtl:rotate-180"
              />
            </button>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {/* See the mobile nav: the skeleton REPLACES the list, because the pre-config list is the
            full one and that is exactly what must not be shown. */}
        {isLoadingModules ? <NavSkeleton collapsed={isCollapsed} /> : modules.map((module) => (
          <div key={module.id}>
            {module.id === firstAdminModuleId && <AdminSectionDivider collapsed={isCollapsed} />}

            {/* Module Header */}
            <button
              data-testid={`nav-module-${module.id}`}
              onClick={() => toggleModule(module.id)}
              className={`w-full flex items-center justify-between px-4 py-3 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                isModuleActive(module)
                  ? 'bg-primary/5 text-primary'
                  : 'text-foreground hover:bg-muted'
              }`}
              title={isCollapsed ? t(module.title) : ''}
            >
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className={`w-8 h-8 bg-gradient-to-br ${module.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon name={module.icon as any} size={16} color="white" />
                </div>
                {!isCollapsed && (
                  <span className="font-medium text-sm">{t(module.title)}</span>
                )}
              </div>
              {!isCollapsed && (
                <Icon
                  name="ChevronDown"
                  size={16}
                  className={`transition-transform ${
                    expandedModules.includes(module.id) ? 'rotate-180' : ''
                  }`}
                />
              )}
            </button>

            {/* Module Body (only when expanded and not collapsed) */}
            {expandedModules.includes(module.id) && !isCollapsed && renderModuleBody(module, false)}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
